/* eslint-disable indent */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Grid, InputLabel, TextField, FormControl, MenuItem, FormHelperText, Select } from '@mui/material'

import { updateFlowStart, cacheUpdateFlowGroupStartAction } from '../../../../../../actions'
import { NONE_VALUE, textFieldTypes, variantTypes } from '../../../../../../constants'
import { Logger, LoggerTypes, checkUpdateResponse, checkErrorResponse } from '../../../../../../utils'
import { FlowElementBottomButtons } from '../../../FlowElementBottomButtons'
import '../../../common-elements-style.scss'
import './style.scss'

const defaultValue = {
  name: '',
  description: '',
  slot_number: 1
}

export const FlowElementStartAboutPanel = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentStart,
  variables,
  // -- refresh the view after saving
  refreshAfterSave
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const nameValue = `${currentStart?.name ? currentStart?.name : ''}` || defaultValue.name
  const descriptionValue = `${currentStart?.description ? currentStart?.description : ''}` || defaultValue.description
  const slotNumberValue = `${currentStart?.slot_number ? currentStart?.slot_number : ''}` || defaultValue.slot_number
  const flowCounterIdValue =
    `${currentStart?.flow_counter ? currentStart?.flow_counter && currentStart?.flow_counter?.id : NONE_VALUE}` ||
    NONE_VALUE

  const [name, setName] = useState(nameValue)
  const [description, setDescription] = useState(descriptionValue)
  const [slot_number, setSlotNumber] = useState(Math.abs(slotNumberValue))
  const [flow_counter_id, setFlowCounterId] = useState(flowCounterIdValue)

  const [errors, setErrors] = useState(null)

  useEffect(() => {
    setName(nameValue)
    setDescription(descriptionValue)
    setSlotNumber(slotNumberValue)
    setFlowCounterId(flowCounterIdValue)
  }, [currentStart])

  const onClickSave = (e) => {
    e.stopPropagation()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null

    const newStart = {
      id: currentStart.id,
      name: name === '' ? null : name,
      description: description === '' ? null : description,
      slot_number: slot_number === '' ? null : slot_number,
      flow_counter_id: flow_counter_id === '' || flow_counter_id === NONE_VALUE ? null : flow_counter_id
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onClickSave()', { newStart })

    dispatch(updateFlowStart(pid, sid, fid, newStart))
      .then(async (res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'FlowElementStartAboutPanel.onClickSave().dispatch(updateFlowStart(...)).then()',
          res
        )
        checkUpdateResponse(res, enqueueSnackbar, t)

        // Now the start has been updated in the back-end we propagate the change in the REDUX state cache
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onSaveTask().cacheUpdateFlowGroupTask()')
        dispatch(cacheUpdateFlowGroupStartAction(currentFlowGroup, res.data))

        // -- remove the previous errors
        await setErrors(null)

        // -- reload the element data.
        if (refreshAfterSave && typeof refreshAfterSave === 'function') {
          refreshAfterSave(currentFlowGroup, [res.data])
        }
      })
      .catch((res) => {
        Logger.trace(
          LoggerTypes.WARN,
          'FlowElementStartAboutPanel.onClickSave().dispatch(updateFlowStart(...)).catch()',
          {
            res
          }
        )

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  // --

  const onChangeName = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onChangeName()', { name: _value })
    setName(_value)
  }

  const onChangeDescription = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onChangeDescription()', {
      description: _value
    })
    setDescription(_value)
  }

  const onSlotNumberChange = (e) => {
    const newSlotNumber = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onSlotNumberChange()', {
      slot_number,
      newSlotNumber
    })

    setSlotNumber(newSlotNumber)
  }

  const onFlowCounterIdChange = (e) => {
    const newFlowCounterId = e.target.value

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onFlowCounterChange()', {
      flow_counter_id,
      newFlowCounterId
    })

    setFlowCounterId(newFlowCounterId)
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementStartAboutPanel.render()', {
    currentStart,
    variables,
    about: {
      name,
      description,
      slot_number,
      flow_counter_id
    },
    errors
  })

  return (
    <div className="FlowElementStartAboutPanel">
      <form>
        {/* == ABOUT == */}
        <Grid container spacing={2} style={{ padding: '30px 10px 20px' }}>
          {/*  name  */}
          <Grid item xs={12} sm={12}>
            <TextField
              type={textFieldTypes.TEXT}
              name="name"
              id="name"
              value={name}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.start.panels.about.name.label')}
              className="common_fullwidth"
              onChange={onChangeName}
              placeholder={t('flowActions.start.panels.about.name.placeholder')}
              error={Boolean(errors && errors?.name)}
              helperText={(errors && errors?.name) || ''}
            />
          </Grid>

          {/*  description  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginTop: '15px' }}>
            <TextField
              type={textFieldTypes.TEXT}
              name="description"
              id="description"
              value={description}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.start.panels.about.description.label')}
              className="common_fullwidth"
              onChange={onChangeDescription}
              placeholder={t('flowActions.start.panels.about.description.placeholder')}
              error={Boolean(errors && errors?.description)}
              helperText={(errors && errors?.description) || ''}
            />
          </Grid>

          {/*  slot_number  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginTop: '15px' }}>
            <TextField
              id="slot_number_value"
              name="slot_number_value"
              label={t('flowActions.start.panels.about.slot_number.label')}
              type={textFieldTypes.NUMBER}
              className="common_fullwidth"
              value={String(Math.abs(slot_number))}
              InputLabelProps={{ shrink: true }}
              onChange={onSlotNumberChange}
              placeholder={t('flowActions.start.panels.about.slot_number.placeholder')}
              variant={variantTypes.OUTLINED}
              error={Boolean(errors && errors?.slot_number)}
              helperText={(errors && errors?.slot_number) || ''}
            />
          </Grid>

          {/*  flow_counter  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginTop: '15px' }}>
            <FormControl error={Boolean(errors && errors?.flow_counter_id)} className="common_fullwidth">
              <InputLabel>{t('flowActions.start.panels.about.flow_counter.label')}</InputLabel>
              <Select
                value={flow_counter_id}
                defaultValue={null}
                className="common_fullwidth"
                onChange={onFlowCounterIdChange}
                variant={variantTypes.OUTLINED}
                label={t('flowActions.start.panels.about.flow_counter.label')}
              >
                <MenuItem value={NONE_VALUE}>{t('flowActions.start.panels.about.flow_counter.default')}</MenuItem>
                {variables &&
                  variables.map((variable, index) => (
                    <MenuItem key={`variable-${index}`} value={variable.id}>
                      {variable.name}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>{(errors && errors?.flow_counter_id) || ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <FlowElementBottomButtons onSave={onClickSave} />
      </form>
    </div>
  )
}

FlowElementStartAboutPanel.displayName = 'FlowElementStartAboutPanel'

FlowElementStartAboutPanel.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentStart: PropTypes.object,
  variables: PropTypes.any,
  // -- Flow's Action
  start: PropTypes.arrayOf(PropTypes.object),
  // -- refresh after saving
  refreshAfterSave: PropTypes.func
}

FlowElementStartAboutPanel.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentStart: null,
  variables: null,
  // -- Flow's Action
  start: [],
  // -- refresh after saving
  refreshAfterSave: null
}
